import { useCallback, useRef, useSyncExternalStore } from 'react';

import {
  nextSitePromoBannerBoundary,
  resolveSitePromoBanner,
  type SitePromoBannerCampaign,
} from '@opendoor/shared-ts/site-promo-banner';

const MAX_TIMEOUT_MS = 2_147_483_647;
const DISMISS_EVENT = 'opendoor:site-promo-banner-dismissed';
const getServerSnapshot = () => null;
const STAGING_SITE_HOST = /^(?:pr-\d+-)?(?:beacon|cosmos)\.apps-staging\.opendoor\.com$/;
const LOCAL_SITE_HOST = /^www\.\d+\.localhost$/;

const dismissalKey = (id: string) => `opendoor.site-promo-banner.dismissed.${id}`;

function isSitePromoBannerHost(hostname: string): boolean {
  return (
    hostname === 'www.opendoor.com' ||
    hostname === 'opendoor.com' ||
    hostname === 'demo.simplersell.com' ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    LOCAL_SITE_HOST.test(hostname) ||
    STAGING_SITE_HOST.test(hostname)
  );
}

function wasDismissed(id: string): boolean {
  try {
    return window.localStorage.getItem(dismissalKey(id)) === '1';
  } catch {
    return false;
  }
}

type SitePromoBannerOptions = {
  campaign: SitePromoBannerCampaign | null;
  fallback?: SitePromoBannerCampaign;
  pathname: string | null;
  articleKeywords?: readonly string[];
};

export function useSitePromoBanner({
  campaign,
  fallback,
  pathname,
  articleKeywords,
}: SitePromoBannerOptions): { campaign: SitePromoBannerCampaign | null; dismiss: () => void } {
  const dismissedIds = useRef(new Set<string>());
  const subscribe = useCallback(
    (onChange: () => void) => {
      if ((!campaign && !fallback) || !isSitePromoBannerHost(window.location.hostname))
        return () => {};

      let timer: number | undefined;
      const schedule = () => {
        window.clearTimeout(timer);
        const nowMs = Date.now();
        const boundaries = [campaign, fallback ?? null]
          .map((candidate) => nextSitePromoBannerBoundary(candidate, nowMs))
          .filter((boundary) => boundary !== null);
        if (boundaries.length > 0) {
          const next = Math.min(...boundaries);
          timer = window.setTimeout(
            () => {
              onChange();
              schedule();
            },
            Math.max(1, Math.min(next - nowMs, MAX_TIMEOUT_MS)),
          );
        }
      };
      const onStorage = (event: StorageEvent) => {
        if (
          (campaign && event.key === dismissalKey(campaign.id)) ||
          (fallback && event.key === dismissalKey(fallback.id))
        )
          onChange();
      };
      schedule();
      window.addEventListener('focus', onChange);
      window.addEventListener('storage', onStorage);
      window.addEventListener(DISMISS_EVENT, onChange);
      return () => {
        window.clearTimeout(timer);
        window.removeEventListener('focus', onChange);
        window.removeEventListener('storage', onStorage);
        window.removeEventListener(DISMISS_EVENT, onChange);
      };
    },
    [campaign, fallback],
  );

  const getSnapshot = useCallback(() => {
    if (!isSitePromoBannerHost(window.location.hostname)) return null;
    const options = { pathname, nowMs: Date.now(), articleKeywords };
    const selected =
      resolveSitePromoBanner({ ...options, campaign }) ??
      resolveSitePromoBanner({ ...options, campaign: fallback ?? null });
    return selected && !dismissedIds.current.has(selected.id) && !wasDismissed(selected.id)
      ? selected
      : null;
  }, [campaign, fallback, pathname, articleKeywords]);
  const visibleCampaign = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const dismiss = useCallback(() => {
    if (!visibleCampaign) return;
    dismissedIds.current.add(visibleCampaign.id);
    try {
      window.localStorage.setItem(dismissalKey(visibleCampaign.id), '1');
    } catch {
      // Closing still works in browsers that block storage.
    }
    window.dispatchEvent(new Event(DISMISS_EVENT));
  }, [visibleCampaign]);

  return { campaign: visibleCampaign, dismiss };
}
